import React, {useState, useEffect, useContext} from "react";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import { Box, Button, Checkbox, Container, FormField, Input, Textarea, StatusIndicator } from "@amzn/awsui-components-react";
import ErrorAlert from "../common/ErrorAlert";
import { StageContext } from "../../contexes/OrderIdContext";
import { Stage } from "../../lusca-search-api/generated-src";
import { StageSelector } from "../redrive/components/StageSelector";
import { FileUploader } from "../redrive/components/FileUploader";
import { useDlqFileValidation } from "./hooks/useDlqFileValidation";
import { useExecuteDlqRedrive } from "./hooks/useExecuteDlqRedrive";
import { useDlqRedriveData } from "./hooks/useDlqRedriveData";
import { useDlqRedriveDetails } from "./hooks/useDlqRedriveDetails";
import { DlqExecutionsTable } from "./table/dlqExecutionsTable";
import { DlqRedriveDetailsTable } from "./table/DlqRedriveDetailsTable";

interface Props {
  setStage: React.Dispatch<React.SetStateAction<Stage>>;
}

export default function DlqRedriveContent({ setStage }: Props) {
  const stage = useContext(StageContext);
  const [error, setError] = useState<string | null>(null);
  const [pks, setPks] = useState<string>('');
  const [mcmId, setMcmId] = useState<string>('');
  const [dryRun, setDryRun] = useState<boolean>(false);
  const [deleteWithoutRedriving, setDeleteWithoutRedriving] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [selectedExecutionArn, setSelectedExecutionArn] = useState<string | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const executionArn = urlParams.get('executionArn');
    if (executionArn) {
      setSelectedExecutionArn(executionArn);
    }
  }, []);

  const { pastRedrives, loading: loadingRedrives, error: fetchError } = useDlqRedriveData(stage);
  const { details, loading: loadingDetails, error: detailsError } = useDlqRedriveDetails(stage, selectedExecutionArn);

  const {
    file,
    messageCount,
    error: fileError,
    redriveOK,
    handleFileChange,
    validateRedrive,
  } = useDlqFileValidation();

  const { executeRedrive } = useExecuteDlqRedrive();

  const handleExecute = async () => {
    if (deleteWithoutRedriving && !mcmId.trim()) {
      setError("MCM ID is required when deleteWithoutRedriving is enabled");
      return;
    }

    if (!pks.trim() && !file) {
      setError("Either DLQ pks or a file must be provided");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const pkList = pks.trim() 
        ? pks.split('\n').map(id => id.trim()).filter(id => id)
        : undefined;

      const result = await executeRedrive({
        stage,
        messageIds: pkList,
        file,
        mcmId: mcmId.trim() || undefined,
        dryRun,
        deleteWithoutRedriving
      });

      setError(null);
      
      if (result?.executionArn) {
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set('executionArn', result.executionArn);
        window.history.pushState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
        setSelectedExecutionArn(result.executionArn);
      } else {
        alert(`DLQ redrive ${dryRun ? 'validation' : 'started execution'} completed successfully`);
      }
    } catch (err) {
      setError(`Failed to execute DLQ redrive: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const canExecute = (pks.trim() || (file && redriveOK));
  const combinedError = [error, fileError, fetchError, detailsError].filter(Boolean).join('; ') || null;

  const handleBackToRedrives = () => {
    setSelectedExecutionArn(null);
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete('executionArn');
    window.history.pushState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
  };

  if (selectedExecutionArn) {
    return (
      <SpaceBetween size="xl">
        <div>
          <SpaceBetween size="l">
            {combinedError && <ErrorAlert content={combinedError} header="DLQ Redrive Error" />}
            <Button
              iconName="arrow-left"
              onClick={handleBackToRedrives}
              variant="link"
            >
              Back to all redrives
            </Button>
            <Container header={<Box variant="h2">DLQ Redrive Execution Details</Box>}>
              <SpaceBetween size="m">
                <Box variant="p">Execution ARN: {selectedExecutionArn}</Box>
                {details && (
                  <Box>
                    Status: <StatusIndicator type={details.status === "COMPLETED" ? "success" : details.status === "PENDING" ? "in-progress" : "error"}>
                      {details.status}
                    </StatusIndicator>
                  </Box>
                )}
                {details?.error && (
                  <Box variant="p" color="text-status-error">Error: {details.error}</Box>
                )}
                {details?.result?.error && (
                  <Box variant="p" color="text-status-error">Error: {details.result.error}</Box>
                )}
                {details?.result?.cause && (
                  <Box variant="p" color="text-status-error">Cause: {details.result.cause}</Box>
                )}
              </SpaceBetween>
            </Container>
            {details && details.result && (
              <DlqRedriveDetailsTable details={details} loading={loadingDetails} />
            )}
          </SpaceBetween>
        </div>
      </SpaceBetween>
    );
  }

  return (
    <SpaceBetween size="xl">
      <div>
        <SpaceBetween size="l">
          {combinedError && <ErrorAlert content={combinedError} header="DLQ Redrive Error" />}

          <Container header={<Box variant="h2">DLQ Redrive Tool</Box>}>
            <SpaceBetween size="m">
              <Box variant="p">
                This tool allows you to redrive messages from the Dead Letter Queue (DLQ).
                You can provide DLQ pks directly or upload a CSV file containing DLQ pks.
                Use dry run mode to validate without actually redriving messages.
              </Box>
              {!selectedExecutionArn && (
                <StageSelector
                  stage={stage}
                  setStage={setStage}
                />
              )}
            </SpaceBetween>
          </Container>

          {!selectedExecutionArn && (
            <Container>
            <SpaceBetween size="m">
              <Box variant="h2">DLQ Redrive Configuration</Box>

              <FormField
                label="DLQ pks"
                description="Enter DLQ pks (primary keys), one per line. Leave empty if uploading a file."
              >
                <Textarea
                  value={pks}
                  onChange={({ detail }) => setPks(detail.value)}
                  placeholder="pk-1&#10;pk-2&#10;pk-3"
                  rows={5}
                  disabled={!!file}
                />
              </FormField>

              <Box variant="p">OR</Box>

              <FileUploader
                file={file}
                handleFileChange={handleFileChange}
                validateRedrive={validateRedrive}
                redriveOK={redriveOK}
                loading={loading}
              />

              {file && redriveOK && (
                <Box>File validated ({messageCount} DLQ pks)</Box>
              )}

              <SpaceBetween size="xs">
                <Checkbox
                  checked={dryRun}
                  onChange={({ detail }) => setDryRun(detail.checked)}
                >
                  Dry Run (validate without executing)
                </Checkbox>

                <Checkbox
                  checked={deleteWithoutRedriving}
                  onChange={({ detail }) => setDeleteWithoutRedriving(detail.checked)}
                >
                  Delete Without Redriving
                </Checkbox>
              </SpaceBetween>

              {deleteWithoutRedriving && (
                <FormField
                  label={
                    <span>
                      MCM ID (Created using <a
                        href="https://mcm.amazon.com/templates/86285ba0-0964-489e-9818-6cf33ad23dd1"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        template
                      </a>
                      )
                    </span>
                  }
                >
                  <Input
                    value={mcmId}
                    onChange={({ detail }) => setMcmId(detail.value)}
                    placeholder="MCM-XXXXXXXX"
                  />
                </FormField>
              )}

              <Button
                onClick={handleExecute}
                variant="primary"
                disabled={!canExecute || loading}
                loading={loading}
              >
                {dryRun ? 'Validate Redrive' : 'Execute Redrive'}
              </Button>
            </SpaceBetween>
          </Container>
          )}

          <DlqExecutionsTable
            items={pastRedrives}
            loading={loadingRedrives}
          />
        </SpaceBetween>
      </div>
    </SpaceBetween>
  );
}
